import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Template from "../app/Template";
import Container from "../layout/Container";
import Card from "../layout/Card";
import SimpleButton from "./SimpleButton";
import { Link } from "react-router-dom";
import AlertModal from "./AlertModal";
import ProcessingModal from "./ProcessingModal";
import { MagnifyingGlassIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { nextPollDelay, POLL_IDLE_MS, POLL_MIN_MS } from "./pollSchedule";

interface ContentItem {
  content_id: string;
  content_type: string;
  status: string;
  title: string;
  author: string;
  created_at: string;
  error_message?: string;
}

const PAGE_SIZE = 10;

/**
 * Statuses that have produced something for the details page to show.
 *
 * `ingested` and `processing` have no tagging or filtering outputs yet — the review
 * endpoint returns them as null — so opening details for one is a dead end.
 */
const hasDetails = (status: string) =>
  status !== "ingested" && status !== "processing";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "ingested", label: "Ingested" },
  { value: "processing", label: "Processing" },
  { value: "review_pending", label: "Pending review" },
  { value: "approved", label: "Approved" },
  { value: "loaded", label: "Published" },
  { value: "rejected", label: "Rejected" },
  { value: "failed", label: "Failed" },
];

const selectClass =
  "w-full sm:min-w-[200px] sm:w-auto px-3 sm:px-4 py-2.5 border-2 border-gray-300 rounded-lg bg-white text-dark-grey text-sm sm:text-base font-medium focus:outline-none focus:ring-2 focus:ring-orange focus:border-orange appearance-none cursor-pointer pr-10 shadow-sm";

const searchInputClass =
  "w-full pl-10 pr-10 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg bg-white text-dark-grey text-sm sm:text-base placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange focus:border-orange shadow-sm";

export default function PipelineDashboard() {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [alertModal, setAlertModal] = useState({
    show: false,
    title: "",
    message: "",
    type: "info" as "success" | "error" | "warning" | "info",
  });
  const [processing, setProcessing] = useState(false);
  const [processingAbortController, setProcessingAbortController] = useState<AbortController | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);

  const contentItemsRef = useRef<ContentItem[]>([]);
  contentItemsRef.current = contentItems;

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch]);

  /**
   * Fetch a page of items and return them.
   *
   * The return value matters for the poller below: `contentItemsRef` is assigned
   * during render, so it is still the *previous* array immediately after this
   * resolves. Comparing against the returned results instead of the ref is what
   * lets the backoff tell "nothing changed" from "changed".
   */
  const fetchContentItems = useCallback(
    async (silent = false): Promise<ContentItem[]> => {
      try {
        if (!silent) setLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("page_size", String(PAGE_SIZE));
        if (statusFilter !== "all") {
          params.set("status", statusFilter);
        }
        if (debouncedSearch) {
          params.set("search", debouncedSearch);
        }
        const url = `/api/pipeline/ingestion/items/?${params.toString()}`;

        const token = localStorage.getItem("access");
        const response = await fetch(url, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        if (response.ok) {
          const data = await response.json();
          const results = Array.isArray(data.results) ? data.results : [];
          setContentItems(results);
          setTotalCount(typeof data.count === "number" ? data.count : results.length);
          const tp = typeof data.total_pages === "number" ? data.total_pages : 1;
          setTotalPages(tp);
          return results;
        }
        setContentItems([]);
        setTotalCount(0);
        setTotalPages(1);
        return [];
      } catch (error) {
        console.error("Error fetching content:", error);
        setContentItems([]);
        setTotalCount(0);
        setTotalPages(1);
        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [page, statusFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchContentItems(false);
  }, [fetchContentItems]);

  useEffect(() => {
    if (totalPages >= 1 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  /**
   * Status polling, self-scheduled with backoff.
   *
   * Two things to preserve here. First, the effect must not depend on
   * `contentItems`: it once did, and since each fetch replaced the array, the
   * effect tore down and re-ran on every response — firing an immediate refetch
   * each time and turning a 3s interval into roughly one request per second.
   * `contentItemsRef` keeps the latest items readable without that feedback loop.
   *
   * Second, a fixed interval hammers the list endpoint at the same rate whether a
   * job is seconds or minutes from finishing. Processing takes 2-3 minutes, so the
   * delay now grows while nothing changes and snaps back the moment something does.
   */
  useEffect(() => {
    const needsLiveStatus = (s: string) => s === "processing" || s === "ingested";
    /** Statuses only; ignores fields that churn without being progress. */
    const signature = (items: ContentItem[]) =>
      items.map((i) => `${i.content_id}:${i.status}`).join("|");

    let timer: number | undefined;
    let cancelled = false;
    let delay = POLL_MIN_MS;

    const schedule = (ms: number) => {
      if (cancelled) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(tick, ms);
    };

    async function tick() {
      if (cancelled) return;

      const visible = document.visibilityState === "visible";
      const live = contentItemsRef.current.some((i) => needsLiveStatus(i.status));

      // Background tabs poll nothing; the visibilitychange handler resumes them.
      // Nothing in flight is the same case: an idle heartbeat that still picks up
      // items ingested in another tab without keeping this one busy.
      if (!visible || !live) {
        delay = POLL_MIN_MS;
        schedule(POLL_IDLE_MS);
        return;
      }

      const before = signature(contentItemsRef.current);
      const results = await fetchContentItems(true);
      if (cancelled) return;

      delay = nextPollDelay(delay, signature(results) !== before);
      schedule(delay);
    }

    // Coming back to the tab should feel immediate, not "up to 30s late".
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      delay = POLL_MIN_MS;
      schedule(0);
    };

    schedule(delay);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchContentItems]);

  const triggerProcessing = async (contentId: string) => {
    const abortController = new AbortController();
    setProcessingAbortController(abortController);
    setProcessing(true);

    try {
      const token = localStorage.getItem("access");
      const response = await fetch("/api/pipeline/processing/run/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ content_id: contentId }),
        signal: abortController.signal,
      });

      if (response.ok) {
        setProcessing(false);
        setAlertModal({
          show: true,
          title: "Processing Complete",
          message: "Content has been processed successfully and is ready for review!",
          type: "success",
        });
        fetchContentItems();
      } else {
        setProcessing(false);
        const error = await response.json();
        setAlertModal({
          show: true,
          title: "Processing Failed",
          message: error.message || "Failed to process content. Please try again.",
          type: "error",
        });
      }
    } catch (error: unknown) {
      setProcessing(false);
      if (error instanceof Error && error.name === "AbortError") {
        setAlertModal({
          show: true,
          title: "Processing Cancelled",
          message: "Content processing was cancelled by user.",
          type: "warning",
        });
      } else {
        console.error("Error triggering processing:", error);
        setAlertModal({
          show: true,
          title: "Network Error",
          message: "Unable to connect to the server. Please check your connection.",
          type: "error",
        });
      }
    }
  };

  const handleCancelProcessing = () => {
    if (processingAbortController) {
      processingAbortController.abort();
      setProcessing(false);
    }
  };

  const publishApproved = async (contentId: string) => {
    setPublishingId(contentId);
    try {
      const token = localStorage.getItem("access");
      const response = await fetch("/api/pipeline/processing/review/load/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ content_id: contentId }),
      });
      const data = await response.json().catch(() => ({}));
      console.info("[pipeline publish] /review/load/ response", data);
      if (response.ok && data.success !== false) {
        const parts = [data.message || "Loaded to production.", data.visibility_note].filter(
          Boolean
        );
        setAlertModal({
          show: true,
          title: data.public_site_visible === false ? "Loaded (not public)" : "Published",
          message: parts.join(" "),
          type: "success",
        });
        fetchContentItems();
      } else {
        setAlertModal({
          show: true,
          title: "Publish Failed",
          message: data.error || data.message || "Could not publish this item.",
          type: "error",
        });
      }
    } catch {
      setAlertModal({
        show: true,
        title: "Network Error",
        message: "Unable to publish. Check your connection.",
        type: "error",
      });
    } finally {
      setPublishingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      ingested: "bg-blue-100 text-blue-800",
      processing: "bg-yellow-100 text-yellow-800",
      processed: "bg-green-100 text-green-800",
      review_pending: "bg-purple-100 text-purple-800",
      approved: "bg-green-200 text-green-900",
      rejected: "bg-red-100 text-red-800",
      failed: "bg-red-200 text-red-900",
      loaded: "bg-emerald-100 text-emerald-900",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const formatStatusLabel = (status: string) => {
    if (status === "loaded") return "PUBLISHED";
    return status.replace(/_/g, " ").toUpperCase();
  };

  const statusFilterLabel = useMemo(
    () => STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? "All statuses",
    [statusFilter]
  );

  const emptyMessage = useMemo(() => {
    if (debouncedSearch) {
      return "No content matches your search. Try different keywords or clear the search box.";
    }
    if (statusFilter !== "all") {
      return `No items with status “${statusFilterLabel}”. Try another filter or choose “All statuses”.`;
    }
    return null;
  }, [debouncedSearch, statusFilter, statusFilterLabel]);

  return (
    <Template navbarMode="solid">
      <Container className="mt-20 sm:mt-24 md:mt-32 mb-12 px-4 sm:px-6 lg:px-8">
        <Card colour="yellow" className="mb-5" paddingX="px-6 sm:px-8" paddingY="py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-dark-grey text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
                Pipeline Dashboard
              </h1>
              <p className="text-dark-grey text-sm sm:text-base">
                Manage content ingestion and processing
              </p>
            </div>
            <Link to="/pipeline/ingest">
              <SimpleButton text="+ Ingest Content" colour="orange" />
            </Link>
          </div>
        </Card>

        <Card colour="white" className="mb-6 shadow-md" paddingX="px-5 sm:px-6 md:px-8" paddingY="py-5 sm:py-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 lg:items-end">
            <div className="flex-1 min-w-0">
              <label htmlFor="pipeline-search" className="block text-dark-grey text-sm font-semibold mb-2">
                Search
              </label>
              <div className="relative">
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <input
                  id="pipeline-search"
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Title, author, or content ID…"
                  className={searchInputClass}
                  autoComplete="off"
                  aria-label="Search pipeline content"
                />
                {searchInput ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-dark-grey"
                    onClick={() => setSearchInput("")}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>

            <div className="w-full lg:w-auto shrink-0">
              <label htmlFor="pipeline-status-filter" className="block text-dark-grey text-sm font-semibold mb-2">
                Status filter
              </label>
              <div className="relative">
                <select
                  id="pipeline-status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={selectClass}
                  aria-label="Filter by pipeline status"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon
                  className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500"
                  aria-hidden
                />
              </div>
            </div>
          </div>
          {!loading && (
            <p className="mt-4 text-xs sm:text-sm text-gray-500">
              {totalCount === 0
                ? "No items to show."
                : `Showing ${contentItems.length ? (page - 1) * PAGE_SIZE + 1 : 0}–${(page - 1) * PAGE_SIZE + contentItems.length} of ${totalCount}`}
            </p>
          )}
        </Card>

        {loading ? (
          <div className="text-center py-16">
            <p className="text-dark-grey text-lg">Loading content items...</p>
          </div>
        ) : contentItems.length === 0 ? (
          <Card colour="white" className="text-center" paddingY="py-16">
            <p className="text-dark-grey text-lg sm:text-xl mb-2">
              {emptyMessage ?? "No content items yet"}
            </p>
            {!debouncedSearch && statusFilter === "all" ? (
              <>
                <p className="text-gray-500 text-sm mb-6">Ingest content to see it listed here.</p>
                <Link to="/pipeline/ingest">
                  <SimpleButton text="Ingest Your First Content" colour="orange" />
                </Link>
              </>
            ) : null}
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 mb-8">
              {contentItems.map((item) => (
                <Card
                  key={item.content_id}
                  colour="white"
                  className="hover:shadow-lg transition-all"
                  paddingX="px-5 sm:px-6 md:px-8"
                  paddingY="py-5 sm:py-6"
                >
                  <div className="flex flex-col lg:flex-row justify-between items-start gap-5">
                    <div className="flex-1 min-w-0 space-y-2.5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <h3 className="text-dark-grey text-lg sm:text-xl md:text-2xl font-bold break-words">
                          {item.title}
                        </h3>
                        <span
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap w-fit ${getStatusColor(
                            item.status
                          )}`}
                        >
                          {formatStatusLabel(item.status)}
                        </span>
                      </div>
                      <div className="space-y-1.5 text-sm sm:text-base">
                        <p className="text-dark-grey">
                          <span className="font-semibold">Author:</span> {item.author}
                        </p>
                        <p className="text-dark-grey">
                          <span className="font-semibold">Type:</span> {item.content_type.toUpperCase()}
                        </p>
                        <p className="text-gray-500 text-xs sm:text-sm">
                          Created: {new Date(item.created_at).toLocaleString()}
                        </p>
                      </div>
                      {item.error_message && (
                        <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-md">
                          <span className="font-semibold">Error:</span> {item.error_message}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:flex-nowrap">
                      {item.status === "ingested" && (
                        <SimpleButton
                          text="Process"
                          colour="orange"
                          onClick={() => triggerProcessing(item.content_id)}
                          className="w-full sm:w-auto"
                        />
                      )}
                      {item.status === "review_pending" && (
                        <Link to={`/pipeline/review/${item.content_id}`} className="w-full sm:w-auto">
                          <SimpleButton text="Review" colour="green" className="w-full" />
                        </Link>
                      )}
                      {item.status === "approved" && (
                        <>
                          <Link to={`/pipeline/review/${item.content_id}`} className="w-full sm:w-auto">
                            <SimpleButton text="Continue review" colour="green" className="w-full" />
                          </Link>
                          <SimpleButton
                            text={publishingId === item.content_id ? "Publishing…" : "Publish to site"}
                            colour="orange"
                            className="w-full sm:w-auto"
                            disabled={publishingId === item.content_id}
                            onClick={() => publishApproved(item.content_id)}
                          />
                        </>
                      )}
                      {/* Details reads tagging and filtering outputs, and neither exists
                          until processing finishes — the page would render an empty
                          shell. Rendered as a plain disabled button rather than a
                          disabled one inside the Link: a disabled <button> inside an
                          anchor still navigates, because the anchor takes the click. */}
                      {hasDetails(item.status) ? (
                        <Link
                          to={`/pipeline/details/${item.content_id}`}
                          className="w-full sm:w-auto"
                        >
                          <SimpleButton text="Details" colour="yellow" className="w-full" />
                        </Link>
                      ) : (
                        <span
                          className="w-full sm:w-auto"
                          title={
                            item.status === "processing"
                              ? "Available once processing finishes"
                              : "Available once this item has been processed"
                          }
                        >
                          <SimpleButton
                            text="Details"
                            colour="yellow"
                            className="w-full"
                            disabled
                          />
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {totalPages > 1 ? (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pb-4">
                <p className="text-dark-grey text-sm font-medium">
                  Page {page} of {totalPages}
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <SimpleButton
                    text="Previous"
                    colour="yellow"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="min-w-[6rem]"
                  />
                  <SimpleButton
                    text="Next"
                    colour="yellow"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="min-w-[6rem]"
                  />
                </div>
              </div>
            ) : null}
          </>
        )}
      </Container>

      <AlertModal
        show={alertModal.show}
        setShow={(show) => setAlertModal({ ...alertModal, show })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      <ProcessingModal
        show={processing}
        title="Processing Content"
        message="Running AI analysis on your content. This usually takes 2-3 minutes. Please don't close this window."
        onCancel={handleCancelProcessing}
      />
    </Template>
  );
}
